import React, { createContext, FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, ConfirmModal } from '@microservices/wiskey-react-components'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { Box, Grid, Typography } from '@mui/material'
import { DataGridProps } from '@mui/x-data-grid'

import { PageContextType } from '@pages/Contexts/types'
import { useDiagrams } from '@pages/Diagrams/hooks'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { ExportActionsPanel } from '@components/ConfigActionsPanel'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { CopyDialog } from '@components/CopyDialog'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { PageTitle } from '@components/PageTitle'
import { ExportAllCheckbox } from '@components/ui/ExportAllCheckbox'

import { EditConfigProvider, useEditConfig } from '@providers/EditConfigProvider'

import { generateConfigSelectColumn, generateDataGridActionsColumn } from '@helpers'
import { CONFIG_MENU_OPTION, ENTITY_CONFIG_TYPE, FLEXIBLE } from '@constants'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const Diagrams = () => {
  const { t } = useTranslation()

  const { configMenuOption, selectedConfigId, handleSelectConfigId } = useEditConfig()

  const columns: DataGridProps['columns'] = [
    {
      field: 'code',
      headerName: t('diagrams.internalId'),
      flex: 1,
    },
    {
      field: 'title',
      headerName: t('diagrams.title'),
      flex: 1,
    },
    {
      field: 'timelineType',
      headerName: t('diagrams.axisX'),
      flex: 1,
    },
  ]

  const {
    state: {
      showCopyDialog,
      showDeleteModal,
      copyData,
      isLoadingCopy,
      isLoadingDelete,
      isLoadingDiagrams,
      diagrams,
    },
    handlers: {
      handleCreateContext,
      handleEditDiagram,
      handleDeleteDiagram,
      handleCloseDeleteModal,
      handleShowDeleteModal,
      handleCopyDiagram,
      handleShowCopyDialog,
      handleCloseCopyDialog,
      handleRowClick,
    },
  } = useDiagrams()

  const contextValue = {
    diagrams,
  }

  const columnsWithActions = useMemo(
    () => [
      ...columns,
      configMenuOption &&
        configMenuOption !== CONFIG_MENU_OPTION.IMPORT &&
        generateConfigSelectColumn({
          selectedConfigId,
          onSelectRow: handleSelectConfigId,
        }),
      generateDataGridActionsColumn({
        onEdit: params => handleEditDiagram(params.row.id),
        onCopy: params => handleShowCopyDialog(params.row.id, params.row.title),
        onDelete: params => handleShowDeleteModal(params.row.id),
      }),
    ],
    [columns, selectedConfigId, configMenuOption]
  )

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigScriptDialog />
      {showCopyDialog && copyData && (
        <CopyDialog
          hideObject
          copyData={copyData}
          dialogTitle={t(`copying.diagram.dialogTitle`)}
          isShow={showCopyDialog}
          loading={isLoadingCopy}
          models={diagrams?.map(diagram => diagram.code) || []}
          onClose={handleCloseCopyDialog}
          onCopy={handleCopyDiagram}
        />
      )}
      {showDeleteModal && (
        <ConfirmModal
          actionBtnText={t('modal.delete.btn')}
          isShow={showDeleteModal}
          text={t('modal.delete.warning')}
          title={t('modal.delete.title')}
          containerStyle={{
            px: 0.5,
            py: 0.5,
            // borderRadius: 0
          }}
          onClose={handleCloseDeleteModal}
          onConfirm={handleDeleteDiagram}
        />
      )}
      <>
        <PageTitle
          end={
            <Box sx={{ py: 0.5 }}>
              <EditConfigMenu />
            </Box>
          }
          start={
            <>
              <Typography display={'inline'} sx={{ mr: 1, ml: 1 }} variant={'h6'}>
                {t('pageTitle.diagrams')}
              </Typography>
            </>
          }
        />
        <PageContentLayout sx={{ ...FLEXIBLE, p: 1 }}>
          <ExportAllCheckbox />
          <Grid container alignItems='center' flexWrap='nowrap' gap={1}>
            <Grid item>
              <Button
                sx={{ mb: 1, width: 150 }}
                variant={'contained'}
                onClick={handleCreateContext}
              >
                {t(`diagrams.add`)}
              </Button>
            </Grid>
            <Grid container item flex={1} flexWrap='nowrap' spacing={2}>
              {/*<FormProvider {...methods}>*/}
              {/*  <Grid item flexGrow={1}>*/}
              {/*    <FormInput*/}
              {/*      inputType={GENERATOR_INPUT_TYPE.INPUT}*/}
              {/*      name='searchPattern'*/}
              {/*      placeholder={t('search')}*/}
              {/*      size='small'*/}
              {/*    />*/}
              {/*  </Grid>*/}
              {/*</FormProvider>*/}
            </Grid>
            <Grid container item flex={1} flexWrap='nowrap' spacing={2}>
              {/*<FormProvider {...methods}>*/}
              {/*  <Grid item flexGrow={1}>*/}
              {/*    <FormInput*/}
              {/*      inputType={GENERATOR_INPUT_TYPE.INPUT}*/}
              {/*      name='searchPattern'*/}
              {/*      placeholder={t('search')}*/}
              {/*      size='small'*/}
              {/*    />*/}
              {/*  </Grid>*/}
              {/*</FormProvider>*/}
            </Grid>
          </Grid>
          <DataGrid
            hideFooter
            columns={columnsWithActions}
            loading={isLoadingDiagrams || isLoadingDelete || isLoadingCopy}
            rows={diagrams || []}
            sx={FLEXIBLE}
            onDelete={handleShowDeleteModal}
            onEdit={handleEditDiagram}
            onRowClick={handleRowClick}
          />
          <ExportActionsPanel />
        </PageContentLayout>
      </>
    </PageContext.Provider>
  )
}

export const DiagramsWithProvider: FC = () => {
  return (
    <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.DIAGRAMS}>
      <Diagrams />
    </EditConfigProvider>
  )
}
